import { action } from '@ember/object';
import EmberService from '@ember/service';

// usage:
// const t = debug('performance').startTimingTracker('someName');
// // do something perf heavy
// t.stop();
//
// after that, we can read the timings with:
// debug('performance').getTimingDataFor('someName');
// or debug('performance').timingData

interface TimingDataItem {
	count: number; // how many times called
	duration: number; // ms spent
}

class TimingTracker {
	key: string;
	startTime: number;
	endTime?: number;
	onStop: (key: string, time: number) => void;

	constructor(key: string, onStop: (key: string, time: number) => void) {
		this.key = key;
		this.onStop = onStop;
		this.startTime = Date.now();
	}

	get duration() {
		const endTime = this.endTime;
		if (!endTime) {
			return 0;
		}
		return endTime - this.startTime;
	}

	stop() {
		this.endTime = Date.now();
		this.onStop(this.key, this.duration);
	}
}

export default class PerformanceService extends EmberService {
	private timingData: Record<string, TimingDataItem> = {};

	getTimingDataFor(key: string) {
		return this.timingData[key];
	}

	startTimingTracker(key: string) {
		return new TimingTracker(key, this.trackTimingData);
	}

	@action
	private trackTimingData(key: string, time: number) {
		if (!this.timingData[key]) {
			this.timingData[key] = { count: 0, duration: 0 };
		}
		this.timingData[key].count++;
		this.timingData[key].duration += time;
	}
}

declare module '@ember/service' {
	interface Registry {
		performance: PerformanceService;
	}
}
